import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from "@angular/common/http";
import {Observable} from "rxjs/internal/Observable";
import {Features, ServiceDetails} from "./service-details";
import {Router} from "@angular/router";
import {User} from "./user";
import {map} from "rxjs/operators";
import { BehaviorSubject, of } from "rxjs/index";
import { MatSnackBar } from "@angular/material/snack-bar";
import {Constants} from "./constants";
import {CommonDataService} from "./common-data.service";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {SiteDetails} from "./site-details";
import {DrupalPlanDetails} from "../my-plan/drupal-plan-details";
import {BpaAction} from "./bpa-action";
import {CallPack} from "../manage-call-pack/call-pack";
import {FetchOptions} from "./fetch-options";
import {OrderDetails} from "./order-details";
import * as moment from "moment-timezone";
import {BillingPeriod} from "../check-usage/billing-period";
import {forkJoin, pipe} from "rxjs";
import {BillingDetails} from "./billing-details";
import { UserLoginDetail } from './user-login-detail';
import {MigratedStaticIp} from "./migrated-static-ip";

@Injectable()
export class CommonService {
  private _mediaBreakpoint: BehaviorSubject<MediaBreakpoint> = new BehaviorSubject(new MediaBreakpoint());
  public mediaBreakpoint$: Observable<MediaBreakpoint> = this._mediaBreakpoint.asObservable();

  public isMobileWindow$: BehaviorSubject<boolean>;

  get mediaBreakpoint(): MediaBreakpoint {
    return this._mediaBreakpoint.getValue();
  }

  constructor(private httpClient: HttpClient, private router:Router, private snackBar: MatSnackBar, private commonDataService: CommonDataService, private breakpointObserver: BreakpointObserver) {
    this.isMobileWindow$ = new BehaviorSubject<boolean>(window.innerWidth < 768 ? true : false);
    window.addEventListener('resize', (evt) => this.isMobileWindow$.next(window.innerWidth < 768 ? true : false));
    this.breakpointObserver.observe([
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge
    ]).subscribe(value => {
      let breakpoint = new MediaBreakpoint();
      if (value.breakpoints[Breakpoints.XSmall]) {
        breakpoint.isMobile = true;
        breakpoint.isTablet = false;
        breakpoint.isDesktop = false;
      }
      if (value.breakpoints[Breakpoints.Small]) {
        breakpoint.isMobile = false;
        breakpoint.isTablet = true;
        breakpoint.isDesktop = false;
      }
      if (value.breakpoints[Breakpoints.Large] || value.breakpoints[Breakpoints.XLarge] || value.breakpoints[Breakpoints.Medium]) {
        breakpoint.isMobile = false;
        breakpoint.isTablet = false;
        breakpoint.isDesktop = true;
      }
      this._mediaBreakpoint.next(breakpoint);
    })
  }

  async checkInflightBpaOrders(){
    await this.getInflightOrderMapping().toPromise().then((result: Map<string, boolean>) => {
      this.commonDataService.bpaInflightMap = result;
    }, error => {
      console.log('failed to get inflight bpa order map: ' + error);
    });
  }

  isBpaInflight(billingAccountId: string) {
    return this.commonDataService.bpaInflightMap[billingAccountId];
  }

  enquireService(msn: string, productType: string): Observable<ServiceDetails> {
    return this.httpClient.get<ServiceDetails>('/api/enquireService/' + msn + "?productType=" + productType);
  }

  getOrderTasks(productOrderId: string): Observable<any> {
    return this.httpClient.get<any>('/api/getOrderTasks/' + productOrderId);
  }


  actionOrderTask(taskId: string): Observable<any> {
    return this.httpClient.get<any>('/api/actionOrderTask/' + taskId);
  }

  getUserDetails():Observable<User>{
    return this.httpClient.get<User>(`/api/user/getUserDetails/${this.commonDataService.user.billingAccountId}`);
  }

  setUserDetails(user: User) {
    user.billingAccountId = this.commonDataService.user.billingAccountId;
    user.userId = this.commonDataService.user.userId;
    this.commonDataService.user = user;
  }

  getServiceDetails():Observable<ServiceDetails[]>{
    return this.httpClient.get<ServiceDetails[]>(`/api/user/getServiceDetails/${this.commonDataService.user.billingAccountId}`);
  }

  getMigratedServiceDetails():Observable<ServiceDetails[]>{
    return this.httpClient.get<ServiceDetails[]>(`/api/user/getMigratedServiceDetails/${this.commonDataService.user.billingAccountId}`);
  }

  getInternodeEmailConfigurationUrl(): Observable<any> {
    return this.httpClient.get<any>('/api/mailbox/getInternodeEmailConfigurationUrl');
  }

  getServiceDetailsByServiceId(serviceId: string): Observable<ServiceDetails> {
    return this.httpClient.get<ServiceDetails>(`/api/user/getServiceDetailsById/${this.commonDataService.user.billingAccountId}/${serviceId}`);
  }

  getLoginPageUrlByBrand(brand: string): Observable<any> {
    return this.httpClient.get<any>('/api/getLoginPage?brand=' + brand);
  }

  getSiteDetailsByBrand(brand: string): Observable<any> {
    return this.httpClient.get<SiteDetails>('/api/getSiteDetails/'+brand);
  }


  getCurrentPlanDetails(): Observable<DrupalPlanDetails>{
    let offerKey = this.commonDataService.selectedServiceDetails.parentServiceKey ? this.commonDataService.selectedServiceDetails.parentServiceKey :this.commonDataService.selectedServiceDetails.serviceKey;
    const params = new HttpParams()
      .append('offerKey', offerKey)
      .append('offerId', this.commonDataService.selectedServiceDetails.offerId)
      .append('isBpa', this.commonDataService.selectedServiceDetails.bpa? 'true':'false')
      .append('serviceId', this.commonDataService.selectedServiceDetails.serviceId);

    return this.httpClient.get<DrupalPlanDetails>('/api/myplan/planDetails', {params : params});
  }

  getBpaActionsForService(serviceId: string): Observable<BpaAction[]>{
    const url = `/api/fetch/getBpaActions/${this.commonDataService.selectedServiceDetails.billingAccountId}/${serviceId}`
    return this.httpClient.get<BpaAction[]>(url);
  }
  getUserCallPacks(): Observable<CallPack[]>{
    return this.httpClient.get<CallPack[]>(`/api/myplan/userCallPacks/${this.commonDataService.selectedServiceDetails.billingAccountId}`);
  }

  getInflightOrderMapping(): Observable<Map<string, boolean>>{
    return this.httpClient.get<Map<string, boolean>>(`/api/dashboard/inflightMap/${this.commonDataService.user.billingAccountId}`)
      .pipe(map( response => {
        const map = new Map<string, boolean>();
        Object.keys(response).forEach(key => {
          map.set(key, response[key]);
        });
        return map;
      }));
  }

  getInflightOrders(): Observable<OrderDetails[]>{
    return this.httpClient.get<OrderDetails[]>(`/api/dashboard/orders/inflight/${this.commonDataService.selectedServiceDetails.billingAccountId}`);
  }

  getAllBpaOrders(): Observable<any>{
    return this.httpClient.get(`/api/dashboard/orders/getAllBpaOrders/${this.commonDataService.selectedServiceDetails.billingAccountId}`,{responseType:'json'});
  }

  getUserFetchOptions(): Observable<FetchOptions>{
    return this.httpClient.get<FetchOptions>(`/api/myplan/userFetchOptions/${this.commonDataService.selectedServiceDetails.billingAccountId}`)
      .pipe(
        map((response: FetchOptions) => {
          if (response) {
            let fetchOption = new FetchOptions();
            Object.assign(fetchOption, response);
            fetchOption.subscribed = true;
            fetchOption.toDelete = false;
            return fetchOption;
          } else {
            return response;
          }
        }
      ));
  }

  redirectToLoginPageByBrand() {
    let returnUrlParameterName = "returnUrl";
    if(this.commonDataService.brand===Constants.BRAND_INTERNODE) {
      returnUrlParameterName = "redirect";
    }
    this.clearSelectedService();
    this.getLoginPageUrlByBrand(this.commonDataService.brand).subscribe((result: any) => {
      (window as any).location.replace(`${result.url}?${returnUrlParameterName}=${location.pathname}`);
    }, error => {
      console.log("unable to retrieve login page url")
    });
  }

  openSnackBar(message: string) {
    this.snackBar.open(message, null, {
      duration: 10000, // 10 seconds
      verticalPosition: "top"
    });
  }

  setPhoneSpacing(space: boolean, phoneNumber: string) {
    let formattedPhoneVal = '';
    if (phoneNumber) {
      let phoneVal = phoneNumber.replace(/\s/g, "");
      formattedPhoneVal = phoneVal;
      if (space) {
        if (phoneVal.length == 10) {
          if (phoneVal.startsWith("04") || phoneVal.startsWith("09")) {
            let a = phoneVal.slice(0, 4);
            let b = phoneVal.slice(4, 7);
            let c = phoneVal.slice(7, 10);
            formattedPhoneVal = a + ' ' + b + ' ' + c;
          } else {
            let a = phoneVal.slice(0, 2);
            let b = phoneVal.slice(2, 6);
            let c = phoneVal.slice(6, 10);
            formattedPhoneVal = a + ' ' + b + ' ' + c;
          }
        }
      }
    }

    return formattedPhoneVal;
  }

  selectService(selectedService: ServiceDetails, shouldRouteToDefault: boolean = true) {
    const shouldForceComponentRefresh = this.commonDataService.selectedServiceDetails
      && selectedService.category === this.commonDataService.selectedServiceDetails.category
      && selectedService.serviceId !== this.commonDataService.selectedServiceDetails.serviceId;

    this.commonDataService.selectedServiceDetails = selectedService;

    if (shouldRouteToDefault) {
      this.routeToDefault()
    }
    if (selectedService.serviceIsBroadband) {
      this.commonDataService.setTechnology(selectedService);
    }
    if (shouldForceComponentRefresh) {
      this.commonDataService.refreshSubject$.next(null);
    }
    localStorage.setItem(Constants.LOCAL_STORAGE_SELECTED_SERVICE_KEY, JSON.stringify(selectedService));
  }

  routeToDefault(): void {
    if (this.commonDataService.user?.migratedTo?.toUpperCase() == 'IINET') {
      this.router.navigate([Constants.DASHBOARD_PATH]);
    } else if (this.commonDataService.selectedServiceDetails.serviceIsFetch) {
      this.router.navigate([Constants.ADD_FETCH_MULTIROOM_PATH]);
    } else if (this.commonDataService.selectedServiceDetails.serviceName.indexOf('Virtual Phone') !== -1) {
      this.router.navigate([Constants.SETTINGS_PATH]);
    } else {
      this.router.navigate([Constants.DASHBOARD_PATH]);
    }
  }

  checkIfCurrentBilling(billingPeriod: BillingPeriod, inclusiveEnd: boolean) {
    const now = moment.tz('Australia/Sydney');
    if (inclusiveEnd) return now.isBetween(moment.parseZone(billingPeriod.validFrom), moment.parseZone(billingPeriod.validTo), 'days', '[]')
    return now.isBetween(moment.parseZone(billingPeriod.validFrom), moment.parseZone(billingPeriod.validTo), 'days', '[)')
  }

  getServiceDetailsByServiceTpe(serviceType: string): Observable<ServiceDetails> {
    return this.httpClient.get<ServiceDetails>(`/api/user/getServiceDetailsByType/${this.commonDataService.selectedServiceDetails.billingAccountId}/${serviceType}`);
  }

  clearSelectedService() {
    localStorage.clear();
  }

  getMobileNumberFromServiceFeatures(features: Features[]): string {
    for (const feature of features) {
      if ((feature?.name.toLowerCase().includes('phone number') || feature?.name.toLowerCase().includes('phonenumber'))
      || (feature?.key.toLowerCase().includes('phone number') || feature?.key.toLowerCase().includes('phonenumber'))) {
        return feature.value.value;
      }
    }
  }

  checkIfCollectionsOrderInflight(): Observable<any> {
    return this.httpClient.get<any>(`/api/dashboard/checkIfCollectionsOrderInflight/${this.commonDataService.user.billingAccountId}`);
  }

  getSwitchAccountRedirectUrl(): Observable<string> {
    return this.httpClient.get<string>(`/api/switch/redirectUrl/${this.commonDataService.brand}/${this.commonDataService.user.billingAccountId}`, { responseType: 'text' as 'json' });
  }

  getBillingAccountDetails(): Observable<BillingDetails> {
    return this.httpClient.get<BillingDetails>(`/api/user/getAccountBillingDetails/${this.commonDataService.user.billingAccountId}`);
  }

  getBillingAccountId(): string {
    if (this.commonDataService.selectedServiceDetails && this.commonDataService.selectedServiceDetails.billingAccountId) {
      return this.commonDataService.selectedServiceDetails.billingAccountId;
    }
    return this.commonDataService.user.billingAccountId;
  }

  getUserLoginDetail(): Observable<UserLoginDetail> {
    return this.httpClient.get<UserLoginDetail>(`/api/user/getUserLoginDetail`);
  }

  getBonusCallDetailsFromServiceFeatures(features: Features[]): string {
    const now = moment.tz('Australia/Sydney');
    for (const feature of features) {
      if ((feature?.name.toLowerCase().includes('Bonus International Calls') || feature?.name.toLowerCase().includes('Bonus International Calls'))) {
        return feature.key;
      }
    }
  }

  getNewStaticIpAddress(): Observable<MigratedStaticIp[]> {
    return this.httpClient.get<MigratedStaticIp[]>(`/api/user/getNewStaticIpAddress/${this.commonDataService.user.billingAccountId}`);
  }

}

export class MediaBreakpoint {
  isMobile: boolean = false;
  isTablet: boolean = false;
  isDesktop: boolean = false;
}
